import type { ThemeConfig } from './types'
import { alpha, darken, lighten } from './colorUtils'

function flattenTokens(obj: Record<string, unknown>, prefix = '--theme'): Record<string, string> {
  const vars: Record<string, string> = {}

  for (const [key, value] of Object.entries(obj)) {
    const cssKey = `${prefix}-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`

    if (key === 'dark' || key === 'meta' || key === 'icons') continue

    if (typeof value === 'string') {
      vars[cssKey] = value
    } else if (typeof value === 'number') {
      vars[cssKey] = String(value)
    } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      if ('type' in value && 'value' in value) {
        // PageBackground — handle specially
        const bg = value as { type: string; value: string; overlay?: string }
        vars[cssKey] = bg.type === 'solid' ? bg.value :
          bg.type === 'gradient' ? bg.value :
          bg.type === 'image' ? `url(${bg.value})` :
          bg.type === 'image-overlay' && bg.overlay
            ? `linear-gradient(${bg.overlay}, ${bg.overlay}), url(${bg.value})`
            : bg.value
      } else {
        Object.assign(vars, flattenTokens(value as Record<string, unknown>, cssKey))
      }
    }
  }

  return vars
}

/**
 * Cores de medalha (ouro/prata/bronze) do pódio, da posição no ranking e das siglas de
 * nível. Semântica fixa — NÃO seguem o white-label (ouro é ouro em qualquer marca) —
 * mas só existem aqui: o CSS das telas consome `var(--theme-medal-*)`, nunca o hex.
 */
const MEDALS = { gold: '#f59e0b', silver: '#94a3b8', bronze: '#c2703d' } as const

/** `--theme-medal-{x}` (sólida, texto branco via `-contrast`), `-subtle` (tint) e `-fg` (texto sobre o tint) — mesma derivação dos tokens de status. */
function medalVars(isDark: boolean): Record<string, string> {
  const vars: Record<string, string> = { '--theme-medal-contrast': '#ffffff' }
  for (const [name, hex] of Object.entries(MEDALS)) {
    vars[`--theme-medal-${name}`] = hex
    vars[`--theme-medal-${name}-subtle`] = alpha(hex, isDark ? 0.18 : 0.12)
    vars[`--theme-medal-${name}-fg`] = isDark ? lighten(hex, 0.15) : darken(hex, 0.12)
  }
  return vars
}

/**
 * Generate backward-compatible CSS variable names that existing components reference.
 * Maps new ThemeConfig structure → legacy --theme-* variable names.
 */
function getLegacyVars(theme: ThemeConfig, isDark: boolean): Record<string, string> {
  const c = theme.colors
  // Cor sólida equivalente do page: temas com page image/gradient não têm um valor
  // válido de background-color (a var --theme-page-background carrega um <image> e
  // computa transparent onde for usada como cor).
  const pageSolid = c.background.page.type === 'solid'
    ? c.background.page.value
    : c.background.page.solidColor ?? (isDark ? '#0f172a' : '#f8fafc')
  // Superfície opaca para overlays mobile: a tint da própria superfície composta
  // sobre a base sólida do page. Em temas de superfícies sólidas o resultado é
  // idêntico à var pura; em temas translúcidos (dark com imagem) vira opaco sem
  // deixar o conteúdo de trás vazar.
  const overlayBg = (tint: string) => `linear-gradient(${tint}, ${tint}), ${pageSolid}`
  return {
    // Page
    '--theme-page-background': typeof c.background.page === 'object' && c.background.page.type === 'solid'
      ? c.background.page.value
      : typeof c.background.page === 'object' && c.background.page.type === 'image-overlay' && c.background.page.overlay
        ? `linear-gradient(${c.background.page.overlay}, ${c.background.page.overlay}), url(${c.background.page.value})`
        : typeof c.background.page === 'object' && c.background.page.type === 'image'
          ? `url(${c.background.page.value})`
          : typeof c.background.page === 'object'
            ? c.background.page.value
            : '',
    '--theme-page-background-color': pageSolid,
    // Overlays mobile (drawer da sidebar, painel de conversa, bottom sheets fixed
    // sobre conteúdo) — usar via `background:` no container raiz do overlay.
    '--theme-overlay-sidebar-background': overlayBg(c.background.sidebar),
    '--theme-overlay-header-background': overlayBg(c.background.header),
    '--theme-overlay-card-background': overlayBg(c.background.card),
    // Sidebar
    '--theme-sidebar-background': c.background.sidebar,
    '--theme-sidebar-text': c.text.secondary,
    '--theme-sidebar-text-hover': c.text.primary,
    '--theme-sidebar-active': c.primary,
    '--theme-sidebar-active-text': c.text.inverse,
    // Item ativo da nav (shell app.html): light = primaryLight + primaryHover;
    // dark = tint translúcida do primary + lavanda (rgba(168,85,247,.15)/#c4b5fd no default)
    '--theme-sidebar-item-active-bg': isDark ? alpha(c.primary, 0.15) : c.primaryLight,
    '--theme-sidebar-item-active-text': isDark ? lighten(c.primary, 0.2) : c.primaryHover,
    '--theme-sidebar-border': c.border.default,
    // Header
    '--theme-header-background': c.background.header,
    '--theme-header-text': c.text.primary,
    '--theme-header-border': c.border.default,
    '--theme-header-button-hover': c.background.elevated,
    // Cards
    '--theme-card-background': c.background.card,
    '--theme-card-text': c.text.secondary,
    '--theme-card-muted': c.text.muted,
    '--theme-card-border': c.border.default,
    '--theme-table-header-background': c.background.card,
    // Primary
    '--theme-primary': c.primary,
    '--theme-primary-background': c.primaryLight,
    // Buttons
    '--theme-button-primary': c.primary,
    '--theme-button-primary-text': c.text.inverse,
    '--theme-button-primary-hover': c.primaryHover,
    '--theme-button-secondary': c.background.elevated,
    '--theme-button-secondary-text': c.text.secondary,
    '--theme-button-secondary-hover': c.border.strong,
    '--theme-button-ghost': 'transparent',
    '--theme-button-ghost-hover': c.background.elevated,
    // Inputs
    '--theme-input-background': c.background.input,
    '--theme-input-text': c.text.primary,
    '--theme-input-border': c.border.input,
    '--theme-input-focus': c.border.focus,
    // General
    '--theme-component-background': c.background.card,
    '--theme-text': c.text.primary,
    '--theme-border': c.border.default,
    '--theme-muted-text': c.text.muted,
    // Notifications
    '--theme-notification-background': c.background.elevated,
    '--theme-notification-text': c.text.primary,
    '--theme-notification-border': c.border.default,
    // Status
    '--theme-error-color': c.error,
    '--theme-success-color': c.success,
    '--theme-warning-color': c.warning,
    '--theme-info-color': c.info,
    // AI Badge
    '--theme-ai-badge-background': c.primaryLight,
    '--theme-ai-badge-text': c.primary,
    // Status: fundo "subtle" + texto "fg" legível (tags/badges do DS) — derivados
    // DINAMICAMENTE das cores de status (que já são white-label via overrides).
    '--theme-success-subtle': alpha(c.success, isDark ? 0.18 : 0.12),
    '--theme-success-fg': isDark ? lighten(c.success, 0.15) : darken(c.success, 0.12),
    '--theme-warning-subtle': alpha(c.warning, isDark ? 0.18 : 0.12),
    '--theme-warning-fg': isDark ? lighten(c.warning, 0.15) : darken(c.warning, 0.12),
    '--theme-error-subtle': alpha(c.error, isDark ? 0.18 : 0.12),
    '--theme-error-fg': isDark ? lighten(c.error, 0.15) : darken(c.error, 0.12),
    '--theme-info-subtle': alpha(c.info, isDark ? 0.18 : 0.12),
    '--theme-info-fg': isDark ? lighten(c.info, 0.15) : darken(c.info, 0.12),
    // Aliases `-background` dos tints de status: ~176 call sites já referenciam
    // esses nomes (ficavam TRANSPARENTES por var indefinida — bug silencioso).
    '--theme-success-background': alpha(c.success, isDark ? 0.18 : 0.12),
    '--theme-warning-background': alpha(c.warning, isDark ? 0.18 : 0.12),
    '--theme-error-background': alpha(c.error, isDark ? 0.18 : 0.12),
    '--theme-info-background': alpha(c.info, isDark ? 0.18 : 0.12),
    // Superfícies extras do DS (afundada / selecionada)
    '--theme-surface-sunken': isDark ? 'rgba(255, 255, 255, 0.04)' : '#f1f5f9',
    '--theme-surface-selected': c.primaryLight,
    // Linha em destaque dentro de um card (ex.: "você" no ranking, hero do vendedor):
    // claro = tint leve do primary; escuro = tom claro TRANSLÚCIDO — o tema escuro é
    // de superfícies translúcidas, e um fundo claro sólido ali vira um bloco ilegível
    // (incidente 2026-08-22: linha "você" branca com texto branco no ranking).
    '--theme-surface-highlight': isDark ? 'rgba(255, 255, 255, 0.08)' : alpha(c.primary, 0.07),
    // Moldura de prévia de mídia (player video/audio/iframe) — sempre escura,
    // igual nos dois temas: é a cor de um player, não uma superfície do DS.
    '--theme-media-frame-background': '#0e0f1a',
    // Medalhas — ver `MEDALS`/`medalVars` acima
    ...medalVars(isDark),
    // Shadows (legacy names)
    '--shadow-xs': theme.shadows.sm,
    '--shadow-sm': theme.shadows.sm,
    '--shadow-md': theme.shadows.md,
    '--shadow-lg': theme.shadows.lg,
    '--shadow-xl': theme.shadows.xl,
    '--shadow-dropdown': theme.shadows.dropdown,
    // Radius (legacy names)
    '--radius-sm': theme.radius.sm,
    '--radius-md': theme.radius.md,
    '--radius-lg': theme.radius.lg,
    '--radius-xl': theme.radius.xl,
    '--radius-full': theme.radius.full,
    // Transitions (legacy names)
    '--transition-fast': theme.transitions.fast,
    '--transition-base': theme.transitions.base,
    '--transition-slow': theme.transitions.slow,
    // Focus ring
    '--ring-color': c.border.focus,
    // Spacing (legacy names)
    '--space-page-x': theme.spacing.page.x,
    '--space-page-y': theme.spacing.page.y,
    '--space-section': theme.spacing.section,
    '--space-card-x': theme.spacing.card.x,
    '--space-card-y': theme.spacing.card.y,
    // ─── Chat Bubble Tokens (P5 Glass) ───────────────────────────────────────
    '--chat-bubble-radius': '10px',
    '--chat-bubble-radius-tail': '3px',

    // Inbound (received)
    '--chat-bubble-in-bg': isDark
      ? 'rgba(255, 255, 255, 0.08)'
      : 'rgba(255, 255, 255, 0.55)',
    '--chat-bubble-in-fg': isDark ? '#f5f3ff' : '#111827',
    '--chat-bubble-in-border': isDark
      ? '1px solid rgba(255, 255, 255, 0.12)'
      : '1px solid rgba(255, 255, 255, 0.4)',
    '--chat-bubble-in-shadow': isDark
      ? '0 8px 24px -8px rgba(0, 0, 0, 0.4), 0 1px 0 rgba(255, 255, 255, 0.08) inset'
      : '0 8px 24px -8px rgba(99, 102, 241, 0.15), 0 1px 0 rgba(255, 255, 255, 0.6) inset',
    '--chat-bubble-in-meta': '#9ca3af',

    // Outbound (sent) — always indigo→violet gradient
    '--chat-bubble-out-bg': 'linear-gradient(135deg, rgba(99, 102, 241, 0.9), rgba(139, 92, 246, 0.9))',
    '--chat-bubble-out-fg': '#ffffff',
    '--chat-bubble-out-border': '1px solid rgba(255, 255, 255, 0.2)',
    '--chat-bubble-out-shadow': '0 8px 24px -4px rgba(99, 102, 241, 0.4), 0 1px 0 rgba(255, 255, 255, 0.3) inset',
    '--chat-bubble-out-meta': 'rgba(255, 255, 255, 0.75)',

    // Reply preview
    '--chat-bubble-reply-bg-in': isDark ? 'rgba(255, 255, 255, 0.14)' : 'rgba(99, 102, 241, 0.08)',
    '--chat-bubble-reply-bg-out': 'rgba(255, 255, 255, 0.15)',
    '--chat-bubble-reply-accent-in': '#6366f1',
    '--chat-bubble-reply-accent-out': 'rgba(255, 255, 255, 0.7)',

    // Forward
    '--chat-bubble-forward-color-in': '#8b5cf6',
    '--chat-bubble-forward-color-out': 'rgba(255, 255, 255, 0.85)',

    // Reactions (inline in meta)
    '--chat-reaction-bg-in': isDark ? 'rgba(255, 255, 255, 0.16)' : '#ffffff',
    '--chat-reaction-border-in': isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)',
    '--chat-reaction-bg-out': 'rgba(255, 255, 255, 0.22)',
    '--chat-reaction-border-out': 'rgba(255, 255, 255, 0.28)',

    // Ticks (status)
    '--chat-ticks-sent': isDark ? 'rgba(255, 255, 255, 0.5)' : '#9ca3af',
    '--chat-ticks-read': '#60a5fa',

    // ─── Header do atendimento (colunas rotuladas) ───────────────────────────
    // Cores VERBATIM do mockup aprovado
    // `DOCS/superpowers/prototypes/2026-08-19-chat-header-mockups.html` (variante 01):
    // claro = `:root`, escuro = `.app[data-app="dark"]`. O tema do app não tem
    // equivalente de `--border-strong` nem de `--text-faint`, e o CSS deste projeto
    // NÃO ramifica em dark (quem ramifica é este mapper) — por isso os quatro moram
    // aqui, junto dos `--chat-bubble-*`, e são consumidos por `.chat-header` em
    // `src/index.css`. Consumidor único: o header do ChatBox.
    '--chat-hdr-sep': isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.10)',
    '--chat-hdr-label': isDark ? '#6b7890' : '#94a3b8',
    '--chat-hdr-chip-bg': isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)',
    '--chat-hdr-chip-fg': isDark ? '#e2e8f0' : '#334155',
  }
}

export function applyThemeToDOM(theme: ThemeConfig, isDark: boolean = false): void {
  const root = document.documentElement

  // New structured CSS variables
  const vars = flattenTokens({
    colors: theme.colors,
    typography: theme.typography,
    radius: theme.radius,
    borders: theme.borders,
    shadows: theme.shadows,
    blur: theme.blur,
    transitions: theme.transitions,
    spacing: theme.spacing,
  })

  // Legacy backward-compatible variables (sistema ÚNICO --theme-*)
  const legacy = getLegacyVars(theme, isDark)

  for (const [prop, value] of Object.entries({ ...vars, ...legacy })) {
    root.style.setProperty(prop, value)
  }

  // Font family directly on body
  document.body.style.fontFamily = theme.typography.fontFamily
}

export function getThemeCSSVars(theme: ThemeConfig): Record<string, string> {
  return flattenTokens({
    colors: theme.colors,
    typography: theme.typography,
    radius: theme.radius,
    borders: theme.borders,
    shadows: theme.shadows,
    blur: theme.blur,
    transitions: theme.transitions,
    spacing: theme.spacing,
  })
}
